import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from "firebase/functions";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithCredential,
} from "firebase/auth";

let appInstance;
let dbInstance;
let functionsInstance;
let authInstance;

// Initialize Firebase App and Emulators
const initializeFirebase = (
  firebaseConfig,
  useEmulator = false,
  emulatorConfig = {}
) => {
  // Ensure Firebase is initialized only once
  if (appInstance) {
    return appInstance;
  }

  appInstance = initializeApp(firebaseConfig);
  // Use persistent local cache so repeat page refreshes are served from IndexedDB instantly.
  // The SDK syncs with the server in the background — no cold-start wait on repeat loads.
  dbInstance = initializeFirestore(appInstance, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
  functionsInstance = getFunctions(appInstance);
  authInstance = getAuth(appInstance);

  if (useEmulator) {
    const host = emulatorConfig.host || "127.0.0.1";
    const authPort = emulatorConfig.authPort || 9099;
    const firestorePort = emulatorConfig.firestorePort || 8080;
    const functionsPort = emulatorConfig.functionsPort || 5001;

    connectAuthEmulator(authInstance, `http://${host}:${authPort}`);
    connectFirestoreEmulator(dbInstance, host, firestorePort);
    connectFunctionsEmulator(functionsInstance, host, functionsPort);

    console.log(`Firebase connected to emulators on host ${host}`);
  }
  return appInstance; // Return the app instance
};

// --- Authentication Functions ---
const firebaseAuth = {
  onAuthStateChanged: (callback) => onAuthStateChanged(authInstance, callback),
  createUserWithEmailAndPassword: (email, password) =>
    createUserWithEmailAndPassword(authInstance, email, password),
  signInWithEmailAndPassword: (email, password) =>
    signInWithEmailAndPassword(authInstance, email, password),
  signInAnonymously: () => signInAnonymously(authInstance),
  signOut: () => signOut(authInstance),
  getCurrentUser: () => authInstance?.currentUser,
  getCurrentUserOrWait: () => {
    if (authInstance?.currentUser) return Promise.resolve(authInstance.currentUser);
    return new Promise((resolve) => {
      let resolved = false;
      let unsubscribe = () => {};
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          unsubscribe();
          resolve(authInstance?.currentUser || null);
        }
      }, 3000);

      unsubscribe = onAuthStateChanged(authInstance, (user) => {
        if (!resolved && user) {
          resolved = true;
          clearTimeout(timeout);
          unsubscribe();
          resolve(user);
        }
      });
    });
  },
  signInWithGoogleCredential: (credential) => {
    return signInWithCredential(authInstance, credential);
  },
};

// Export raw instance for modular functions like setPersistence
export { authInstance };

// --- Restaurant & Menu Functions ---
const getPublicRestaurant = async (restaurantId) => {
  try {
    const restaurantRef = doc(dbInstance, "private_restaurants", restaurantId);
    const docSnap = await getDoc(restaurantRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return {
        success: false,
        error: "Restaurant not found",
        code: "NOT_FOUND",
      };
    }
  } catch (error) {
    console.error("Firebase getPublicRestaurant error:", error);
    return {
      success: false,
      error: `Failed to fetch restaurant data: ${error.message}`,
      code: "FIREBASE_FETCH_ERROR",
    };
  }
};

const getMenuItems = (restaurantId, callback) => {
  try {
    const menuItemsRef = collection(
      dbInstance,
      "private_restaurants",
      restaurantId,
      "menu_items"
    );
    const q = menuItemsRef;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback({ success: true, data: items });
      },
      (error) => {
        console.error("Firebase getMenuItems snapshot error:", error);
        callback({
          success: false,
          error: `Failed to fetch menu items: ${error.message}`,
        });
      }
    );
    return unsubscribe;
  } catch (error) {
    console.error("Firebase getMenuItems error:", error);
    callback({
      success: false,
      error: `Failed to fetch menu items: ${error.message}`,
    });
  }
};

const getCustomerReviews = async (restaurantId) => {
  try {
    const reviewsRef = collection(dbInstance, "restaurant_reviews_collection");
    const q = query(reviewsRef, where("restaurant_id", "==", restaurantId));
    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: reviews };
  } catch (error) {
    console.error("Firebase getCustomerReviews error:", error);
    return {
      success: false,
      error: `Failed to fetch customer reviews: ${error.message}`,
      code: "FIREBASE_FETCH_ERROR",
    };
  }
};

const getRestaurantTheme = async (restaurantId) => {
  try {
    const themeRef = doc(dbInstance, "private_restaurants", restaurantId, "restaurant_config", "theme");
    const docSnap = await getDoc(themeRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return {
        success: false,
        error: "Theme not found",
        code: "NOT_FOUND",
      };
    }
  } catch (error) {
    console.error("Firebase getRestaurantTheme error:", error);
    return {
      success: false,
      error: `Failed to fetch theme data: ${error.message}`,
      code: "FIREBASE_FETCH_ERROR",
    };
  }
};

// --- Cart Functions (Removed - now handled by local storage) ---
// getCartItems, addCartItem, updateCartItem, removeCartItem, clearCart

const applyPromoCode = async (code) => {
  try {
    const applyPromo = httpsCallable(functionsInstance, "applyPromoCode");
    const result = await applyPromo({ code });
    return result.data;
  } catch (error) {
    console.error("Firebase applyPromoCode error:", error);
    return {
      success: false,
      error: error.message,
      code: "FIREBASE_FUNCTION_ERROR",
    };
  }
};

const submitOrder = async (orderData) => {
  try {
    const createOrderCallable = httpsCallable(functionsInstance, 'createOrder');
    const result = await createOrderCallable(orderData);
    return { success: true, data: result.data, message: "Order placed successfully" };
  } catch (error) {
    console.error("Firebase submitOrder (Cloud Function) error:", error);
    // Cloud Functions errors are wrapped in HttpsError, so we extract the details
    const errorMessage = error.message || "Failed to submit order via Cloud Function.";
    const errorCode = error.code || "FIREBASE_CLOUD_FUNCTION_ERROR";
    return {
      success: false,
      error: errorMessage,
      code: errorCode,
    };
  }
};


// create unified order
const createUnifiedOrder = async (orderData) => {
  try {
    // This calls the NEW Cloud Function we will write in Step 2
    const callable = httpsCallable(functionsInstance, "createUnifiedOrder");
    const result = await callable(orderData);

    // Returns: { razorpay_order_id, amount, currency, firestore_order_id }
    return { success: true, data: result.data };
  } catch (error) {
    console.error("Firebase createUnifiedOrder error:", error);
    return {
      success: false,
      error: error.message || "Failed to initiate payment",
      code: error.code || "PAYMENT_INIT_ERROR",
    };
  }
};





// ---------------------------------------------------------
// 🆕 1. Create Pending Order (Calls Cloud Function)
// ---------------------------------------------------------
const createPendingOrder = async (orderData) => {
  try {
    // This calls the NEW Cloud Function we will write in Step 2
    const callable = httpsCallable(functionsInstance, 'createPendingOrder');
    const result = await callable(orderData);

    // Returns: { razorpay_order_id, amount, currency, firestore_order_id }
    return { success: true, data: result.data };
  } catch (error) {
    console.error("Firebase createPendingOrder error:", error);
    return {
      success: false,
      error: error.message || "Failed to initiate payment",
      code: error.code || "PAYMENT_INIT_ERROR",
    };
  }
};


// ---------------------------------------------------------
// 🆕 2. Listen to Order Status (Real-time Webhook Confirmation)
// ---------------------------------------------------------
const listenToOrderPaymentStatus = (restaurantId, orderId, callback) => {
  try {
    const orderRef = doc(dbInstance, "private_restaurants", restaurantId, "orders_collection", orderId);

    const unsubscribe = onSnapshot(orderRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        // Callback triggers whenever the document changes (e.g., when Webhook updates it to 'paid')
        callback({ success: true, data: data });
      } else {
        callback({ success: false, error: "Order document not found" });
      }
    }, (error) => {
      console.error("Listener Error:", error);
      callback({ success: false, error: error.message });
    });

    return unsubscribe; // Return cleanup function
  } catch (error) {
    console.error("Firebase listenToOrderPaymentStatus error:", error);
    return () => { }; // Return dummy cleanup
  }
};

const getOrdersByUserId = (userId, restaurantId, callback) => {
  try {
    const ordersRef = collection(dbInstance, "private_restaurants", restaurantId, "orders_collection");
    const q = query(ordersRef, where("user_id", "==", userId), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date(), // Convert Firebase Timestamp to Date object
      }));
      callback({ success: true, data: orders });
    });

    return unsubscribe;
  } catch (error) {
    console.error("Firebase getOrdersByUserId error:", error);
    callback({
      success: false,
      error: "Failed to fetch orders",
      code: "FIREBASE_FETCH_ERROR",
    });
  }
};

const updateOrderStatus = async (orderId, newStatus, restaurantId) => {
  try {
    const orderRef = doc(dbInstance, "private_restaurants", restaurantId, "orders_collection", orderId);
    await updateDoc(orderRef, { orderStatus: newStatus, updatedAt: new Date() });
    return { success: true, message: "Order status updated successfully" };
  } catch (error) {
    console.error("Firebase updateOrderStatus error:", error);
    return {
      success: false,
      error: "Failed to update order status",
      code: "FIREBASE_UPDATE_ERROR",
    };
  }
};

// ---------------------------------------------------------
// 🆕 3. Listen to Cart Hold Status (Show Code Feature)
// ---------------------------------------------------------
const listenToCartHoldStatus = (restaurantId, cartHoldId, callback) => {
  try {
    const cartHoldRef = doc(dbInstance, "restaurants", restaurantId, "cartHolds", cartHoldId);

    const unsubscribe = onSnapshot(cartHoldRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        callback({ success: true, data: data });
      } else {
        callback({ success: false, error: "Cart hold document not found" });
      }
    }, (error) => {
      console.error("Cart Hold Listener Error:", error);
      callback({ success: false, error: error.message });
    });

    return unsubscribe;
  } catch (error) {
    console.error("Firebase listenToCartHoldStatus error:", error);
    return () => { }; 
  }
};

export {
  initializeFirebase,
  firebaseAuth,
  getPublicRestaurant,
  getMenuItems,
  getCustomerReviews,
  getRestaurantTheme,
  applyPromoCode,
  submitOrder,
  getOrdersByUserId,
  updateOrderStatus,
  listenToOrderPaymentStatus,
  createPendingOrder,
  createUnifiedOrder,
  listenToCartHoldStatus
};
